import { useEffect, useState } from 'react'
import { portalAPI, roomsAPI } from '../lib/api'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'
import {
  Calendar, Clock, MapPin, QrCode, X, Plus, CheckCircle,
  Award, User, ArrowRight, Sparkles, AlertCircle, CalendarDays
} from 'lucide-react'

export default function PortalBookingsPage() {
  const [customer, setCustomer] = useState<{ id: number; name: string; email: string; loyalty_points: number } | null>(null)
  const [bookings, setBookings] = useState<any[]>([])
  const [rooms, setRooms] = useState<any[]>([])
  const [roomId, setRoomId] = useState<number | ''>('' as any)
  const [date, setDate] = useState<string>('')
  const [start, setStart] = useState<string>('')
  const [end, setEnd] = useState<string>('')
  const [msg, setMsg] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [showNewBooking, setShowNewBooking] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    const [sessionRes, roomsRes, bookingsRes] = await Promise.all([
      portalAPI.session(),
      roomsAPI.list(),
      portalAPI.listBookings()
    ])

    if (sessionRes.success) setCustomer(sessionRes.data!.customer as any)
    if (roomsRes.success) setRooms(roomsRes.data as any[])
    if (bookingsRes.success) setBookings(bookingsRes.data as any[])
    setLoading(false)
  }

  async function createBooking(e: React.FormEvent) {
    e.preventDefault()
    setMsg('')
    setError('')
    if (!roomId || !date || !start || !end) {
      setError('Please fill in all fields')
      return
    }

    setLoading(true)
    const startIso = new Date(`${date}T${start}:00`).toISOString()
    const endIso = new Date(`${date}T${end}:00`).toISOString()
    const res = await portalAPI.createBooking({ room_id: Number(roomId), start_time: startIso, end_time: endIso })
    setLoading(false)

    if (res.success) {
      setBookings(prev => [...prev, res.data])
      setMsg('Booking created successfully!')
      setRoomId('')
      setDate('')
      setStart('')
      setEnd('')
      setShowNewBooking(false)
    } else {
      setError(res.message || 'Failed to create booking')
    }
  }

  async function cancel(id: number) {
    if (!confirm('Are you sure you want to cancel this booking?')) return
    setLoading(true)
    const res = await portalAPI.cancelBooking(id)
    setLoading(false)
    if (res.success) {
      setBookings(prev => prev.filter(b => b.id !== id))
      setMsg('Booking cancelled successfully')
    } else {
      setError('Failed to cancel booking')
    }
  }

  async function showQr(id: number) {
    const res = await portalAPI.getQrCode(id)
    if (res.success) {
      const code = res.data!.code
      window.location.href = `/portal/qr?code=${code}`
    }
  }

  const upcomingBookings = bookings.filter(b => b.status !== 'cancelled' && new Date(b.start_time) > new Date())
  const pastBookings = bookings.filter(b => b.status === 'cancelled' || new Date(b.start_time) <= new Date())

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              My Bookings
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your studio bookings and check-ins</p>
          </div>

          {/* User Info Card */}
          {customer && (
            <div className="card bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 border-orange-200 dark:border-orange-800">
              <div className="card-body py-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-red-500">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{customer.name}</p>
                    <div className="flex items-center gap-2 text-sm">
                      <Award className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      <span className="font-medium text-orange-700 dark:text-orange-300">{customer.loyalty_points ?? 0} Points</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        {msg && (
          <div className="p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
            <p className="text-sm font-medium text-green-800 dark:text-green-200">{msg}</p>
          </div>
        )}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* New Booking Button */}
        {!showNewBooking && (
          <Button
            onClick={() => setShowNewBooking(true)}
            className="w-full md:w-auto bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white flex items-center justify-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Create New Booking
          </Button>
        )}

        {/* New Booking Form */}
        {showNewBooking && (
          <div className="card border-2 border-orange-200 dark:border-orange-800">
            <div className="card-header flex items-center justify-between bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10">
              <div className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-orange-600" />
                <h2 className="font-semibold text-lg">Create New Booking</h2>
              </div>
              <Button
                variant="outline"
                onClick={() => {setShowNewBooking(false); setError(''); setMsg('')}}
                className="flex items-center gap-1"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="card-body">
              <form onSubmit={createBooking} className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="form-label flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    Select Room
                  </label>
                  <Select
                    value={roomId}
                    onChange={(e) => setRoomId(Number(e.target.value))}
                    required
                    disabled={loading}
                  >
                    <option value="">Choose a room...</option>
                    {rooms.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="form-label flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    Date
                  </label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      Start
                    </label>
                    <Input
                      type="time"
                      value={start}
                      onChange={(e) => setStart(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      End
                    </label>
                    <Input
                      type="time"
                      value={end}
                      onChange={(e) => setEnd(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-800">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {setShowNewBooking(false); setError(''); setMsg('')}}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !roomId || !date || !start || !end}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    {loading ? 'Creating...' : 'Create Booking'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Upcoming Bookings */}
        <div className="card">
          <div className="card-header flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-orange-600" />
            <h2 className="font-semibold text-lg">Upcoming Bookings</h2>
            <span className="ml-auto px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300">
              {upcomingBookings.length}
            </span>
          </div>
          <div className="card-body">
            {loading && upcomingBookings.length === 0 ? (
              <div className="space-y-3">
                {[1, 2].map(i => (
                  <div key={i} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl animate-pulse">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                  </div>
                ))}
              </div>
            ) : upcomingBookings.length === 0 ? (
              <div className="text-center py-12">
                <CalendarDays className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Upcoming Bookings</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create your first booking to get started
                </p>
                <Button
                  onClick={() => setShowNewBooking(true)}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Booking
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingBookings.map(b => (
                  <div
                    key={b.id}
                    className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-500/10">
                            <Calendar className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-lg">
                              {new Date(b.start_time).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(b.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                              <ArrowRight className="h-3 w-3" />
                              {new Date(b.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            b.status === 'confirmed'
                              ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-300'
                              : 'bg-yellow-100 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-300'
                          }`}>
                            {b.status}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => showQr(b.id)}
                          className="flex items-center gap-2"
                        >
                          <QrCode className="h-4 w-4" />
                          QR Code
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => cancel(b.id)}
                          disabled={loading}
                          className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center gap-2"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Past Bookings */}
        {pastBookings.length > 0 && (
          <div className="card">
            <div className="card-header flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-600" />
              <h2 className="font-semibold text-lg">Past Bookings</h2>
              <span className="ml-auto px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-500/10 text-gray-700 dark:text-gray-300">
                {pastBookings.length}
              </span>
            </div>
            <div className="card-body">
              <div className="space-y-2">
                {pastBookings.slice(0, 5).map(b => (
                  <div
                    key={b.id}
                    className="p-3 rounded-lg border border-gray-200 dark:border-gray-800 opacity-75"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <div className="text-sm">
                          <div className="font-medium">
                            {new Date(b.start_time).toLocaleDateString('en-GB')} at {new Date(b.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        b.status === 'cancelled'
                          ? 'bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-300'
                          : 'bg-gray-100 dark:bg-gray-500/10 text-gray-700 dark:text-gray-300'
                      }`}>
                        {b.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
